import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstrumentClock from '../components/InstrumentClock';
import LoginForm from './Login';
import RegisterForm from './Register';
import './auth.css';

function LiveTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <>{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</>
  );
}

/* Shared shell for /login and /register. Both routes render this same
   component, so the brand side (and the ticking clock) stays mounted. On
   switch, the whole clock panel glides across to the other half of the
   screen while the two form panes crossfade in place.

   Both forms stay mounted in the same grid cell: the column keeps the height
   of the taller form (no layout shift), typed values survive a switch, and
   the exit/enter animations overlap so there is never a dead frame. The
   inactive pane is inert, so it's invisible to keyboard and screen readers. */
export default function AuthPage({ mode, authMessage, clearAuthMessage }) {
  const navigate = useNavigate();
  // False until the first switch: on initial load the panes are simply
  // shown/hidden so only the entrance animations play, no crossfade.
  const [swapped, setSwapped] = useState(false);
  const prevMode = useRef(mode);
  const loginPane = useRef(null);
  const registerPane = useRef(null);

  useEffect(() => {
    if (prevMode.current !== mode) {
      prevMode.current = mode;
      setSwapped(true);
    }
    // Move focus to the active form's first field. (Replaces autoFocus,
    // which can't arbitrate with both forms permanently mounted.)
    const pane = mode === 'login' ? loginPane.current : registerPane.current;
    const input = pane && pane.querySelector('input');
    if (input) input.focus({ preventScroll: true });
  }, [mode]);

  const switchTo = (m) => {
    if (m !== mode) navigate(m === 'register' ? '/register' : '/login');
  };

  // Each pane keeps one horizontal side: login animates on the left edge,
  // register on the right, matching the direction the clock panel travels.
  const paneClass = (paneMode, dir) => {
    const active = mode === paneMode;
    const anim = swapped ? ` auth-pane-${active ? 'in' : 'out'}-${dir}` : '';
    return `auth-pane${anim}${active ? '' : ' is-hidden'}`;
  };

  return (
    <div className={`auth-page${mode === 'register' ? ' is-register' : ''}`}>
      <aside className="auth-brand">
        <div className="auth-brand-logo">
          <img src="onshifts.png" alt="" />
        </div>
        <div className="auth-brand-center">
          <div className="auth-clock-wrap" aria-hidden="true">
            <InstrumentClock />
          </div>
          <p className="auth-live" aria-hidden="true">
            It&rsquo;s <LiveTime /> &mdash; do you know who&rsquo;s on shift?
          </p>
          <div className="auth-mode-toggle" role="tablist" aria-label="Log in or register">
            <span className={`auth-mode-glider${mode === 'register' ? ' register' : ''}`} aria-hidden="true" />
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              aria-controls="auth-pane-login"
              className={mode === 'login' ? 'active' : ''}
              onClick={() => switchTo('login')}
            >
              Log in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              aria-controls="auth-pane-register"
              className={mode === 'register' ? 'active' : ''}
              onClick={() => switchTo('register')}
            >
              Register
            </button>
          </div>
        </div>
        <div className="auth-brand-foot" aria-hidden="true">
          <span className="quote">Run your team like clockwork.</span>
          <span className="site">onshifts.ca</span>
        </div>
      </aside>

      <main className="auth-form-side">
        <div className="auth-card">
          <img src="onshifts.png" alt="OnShifts" className="auth-card-logo auth-rise" />
          <div className="auth-card-stack">
            <div
              id="auth-pane-login"
              ref={loginPane}
              inert={mode !== 'login' || undefined}
              className={paneClass('login', 'left')}
            >
              <LoginForm
                authMessage={authMessage}
                clearAuthMessage={clearAuthMessage}
                onSwitch={() => switchTo('register')}
              />
            </div>
            <div
              id="auth-pane-register"
              ref={registerPane}
              inert={mode !== 'register' || undefined}
              className={paneClass('register', 'right')}
            >
              <RegisterForm onSwitch={() => switchTo('login')} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
